import { Component, h, Host, Prop } from '@stencil/core';

/**
 * eSignature features grid section for SignNow brand.
 * Displays key eSignature capabilities in a responsive card grid.
 * Supports 3, 4, or 6 cards per row (desktop) via the `columns` prop.
 * All content is inline-editable via pure slot pass-through.
 *
 * @product signnow
 * @source solutions
 * @version 2.0.0
 * @category SignNow
 * @slot heading - <me-heading tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section title
 * @slot cards - {required}, {multiple}, <features-card-type-6 itemscope itemtype="https://schema.org/ListItem" itemprop="itemListElement"> - Feature card items
 * @example
 *   <sn-esignature-features-type-1 columns="6">
 *     <me-heading slot="heading" variant="3" align="center" tag="h2">Accelerate your document workflows with eSignature by airSlate SignNow</me-heading>
 *     <features-card-type-6 slot="cards" itemscope itemtype="https://schema.org/ListItem" itemprop="itemListElement">
 *       <meta itemprop="position" content="1" />
 *       <me-icon slot="icon" name="https://marketing-static.signnow.com/347/images/me/esignature-features/icon-1.svg"></me-icon>
 *       <me-heading slot="heading" variant="5" tag="h3">Boost paperwork</me-heading>
 *       <me-text slot="description">Place electronic signatures and send records in moments instead of hours.</me-text>
 *     </features-card-type-6>
 *     <features-card-type-6 slot="cards" itemscope itemtype="https://schema.org/ListItem" itemprop="itemListElement">
 *       <meta itemprop="position" content="2" />
 *       <me-icon slot="icon" name="https://marketing-static.signnow.com/347/images/me/esignature-features/icon-2.svg"></me-icon>
 *       <me-heading slot="heading" variant="5" tag="h3">Modify samples safely</me-heading>
 *       <me-text slot="description">Take advantage of the signature tips function and improve your files securely.</me-text>
 *     </features-card-type-6>
 *   </sn-esignature-features-type-1>
 */
@Component({
  tag: 'sn-esignature-features-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnEsignatureFeatures {
  /**
   * Color mode for theming
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Number of cards per row on desktop (2, 3, 4, or 6). Applies the corresponding CSS modifier.
   * @default 6
   */
  @Prop() columns: 2 | 3 | 4 | 6 = 6;

  render() {
    const modifiers: Record<number, string> = {
      2: 'sn-esignature-features__cards--double',
      3: 'sn-esignature-features__cards--triple',
      4: 'sn-esignature-features__cards--quad',
      6: 'sn-esignature-features__cards--six',
    };
    const modifier = modifiers[this.columns] || 'sn-esignature-features__cards--six';

    return (
      <Host data-color-mode={this.colorMode}>
        <me-section class="sn-esignature-features" yGap="2xl" yGapLg="5xl" role="region" aria-label="Features">
          <me-row direction="column" yGap="2xl" yGapLg="3xl">
            <me-column span="12">
              <slot name="heading"></slot>
            </me-column>
            <me-column span="12">
              <div class={`sn-esignature-features__cards ${modifier}`} itemScope itemType="https://schema.org/ItemList">
                <slot name="cards"></slot>
              </div>
            </me-column>
          </me-row>
        </me-section>
      </Host>
    );
  }
}
